/* global google */
import { useEffect, useRef, useState } from 'react';
import styles from './NmiGooglePayButton.module.sass';

const NmiGooglePayButton = ({ checkoutTotal, onApprove, orderData, offer }) => {
  const paymentsClientRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const buttonContainerRef = useRef(null);

  useEffect(() => {
    // Check if Google Pay script is loaded
    if (typeof google === 'undefined' || !google.payments) {
      console.error('Google Pay script not loaded');
      return;
    }

    // Use TEST environment on localhost, or if no production Merchant ID is provided
    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      !offer.googlePayMerchantId;

    const paymentsClient = new google.payments.api.PaymentsClient({
      environment: isLocalhost ? 'TEST' : 'PRODUCTION',
    });
    paymentsClientRef.current = paymentsClient;

    // Manual Readiness Check for NMI
    const readinessRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            allowedCardNetworks: ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA'],
          },
        },
      ],
    };

    paymentsClient
      .isReadyToPay(readinessRequest)
      .then((response) => {
        if (response.result) {
          setIsReady(true);
          if (buttonContainerRef.current) {
            const button = paymentsClient.createButton({
              onClick: handleGooglePayClick,
              buttonColor: 'black',
              buttonType: 'plain',
              buttonSizeMode: 'fill',
            });
            buttonContainerRef.current.innerHTML = '';
            buttonContainerRef.current.appendChild(button);
          }
        }
      })
      .catch((err) => {
        console.error('Error checking Google Pay readiness (NMI):', err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer]);

  const handleGooglePayClick = () => {
    const paymentsClient = paymentsClientRef.current;

    if (!paymentsClient) {
      console.error('Google Pay client not ready');
      return;
    }

    const merchantName = offer.googlePayMerchantName || offer.brand || 'Store';
    const googlePayMerchantId = offer.googlePayMerchantId || '';

    // LOGGING FOR DEBUGGING
    // eslint-disable-next-line no-console
    console.log('--- NMI Google Pay Debug ---');
    // eslint-disable-next-line no-console
    console.log('Hostname:', window.location.hostname);
    // eslint-disable-next-line no-console
    console.log('Environment:', paymentsClientRef.current?.environment);
    // eslint-disable-next-line no-console
    console.log('Google Pay Merchant ID:', googlePayMerchantId);
    // eslint-disable-next-line no-console
    console.log('NMI Public Security Key:', offer.nmiPublicSecurityKey);

    const safeTotal = checkoutTotal && !isNaN(checkoutTotal) ? String(checkoutTotal) : '0.01';
    const safeCurrency = offer.currency || 'USD';

    const paymentDataRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            allowedCardNetworks: ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA'],
            billingAddressRequired: true,
            billingAddressParameters: {
              format: 'FULL',
              phoneNumberRequired: true,
            },
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: {
              gateway: 'gatewayservices',
              gatewayMerchantId: offer.nmiPublicSecurityKey || '',
            },
          },
        },
      ],
      merchantInfo: {
        merchantName: merchantName,
        ...(googlePayMerchantId && { merchantId: googlePayMerchantId }),
      },
      transactionInfo: {
        currencyCode: safeCurrency,
        totalPriceStatus: 'FINAL',
        totalPrice: safeTotal,
      },
      emailRequired: true,
      shippingAddressRequired: true,
      shippingAddressParameters: {
        phoneNumberRequired: true,
      },
    };

    paymentsClient
      .loadPaymentData(paymentDataRequest)
      .then((paymentData) => {
        const shippingAddress = paymentData.shippingAddress || {};
        const email = paymentData.email || '';
        const fullName = shippingAddress.name || '';
        const nameParts = fullName.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        // Extract raw token for NMI
        const rawToken = paymentData.paymentMethodData?.tokenizationData?.token;

        const newOrderData = {
          ...orderData,
          shippingCity: shippingAddress.locality || '',
          shippingAddress1: shippingAddress.address1 || '',
          shippingAddress2: shippingAddress.address2 || '',
          firstName: firstName,
          lastName: lastName,
          email: email,
          shippingZip: shippingAddress.postalCode || '',
          shippingState: shippingAddress.administrativeArea || '',
          shippingCountry: shippingAddress.countryCode?.toUpperCase() || 'US',
          phone: shippingAddress.phoneNumber?.replace(/^\+1/, '').replace(/\s/g, '') || '',
          wallet_token: rawToken,
          creditCardType: 'googlepay',
        };

        onApprove(newOrderData);
      })
      .catch((err) => {
        if (err.statusCode === 'CANCELED') {
          // eslint-disable-next-line no-console
          console.log('Google Pay cancelled by user');
        } else {
          console.error('Error loading Google Pay (NMI):', err);
        }
      });
  };

  if (!isReady) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div ref={buttonContainerRef} className={styles.buttonWrapper} />
    </div>
  );
};

export default NmiGooglePayButton;
